import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, FileText, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';

const LAST_UPDATED = 'May 20, 2026';

const SECTIONS = [
  {
    id: 'introduction',
    title: 'Introduction',
    body: [
      'Welcome to Go Digital Step. By using our services, you agree to comply with and be bound by these Terms and Conditions. Please read them carefully before engaging our services.',
      'If you have any questions about these terms, contact us at info@godigitalstep.com.',
    ],
  },
  {
    id: 'services',
    title: 'Services',
    body: [
      'Go Digital Step offers digital marketing services including Google Maps Marketing, search engine optimization, content creation, paid advertising, website development, and related consulting or campaign services.',
    ],
  },
  {
    id: 'prior-conditions',
    title: 'Prior Conditions',
    body: [
      'Before SEO or digital marketing work begins, the client must ensure that the website and related properties do not contain duplicate sites, duplicate content, redirects, doorway pages, or other practices that may harm search performance.',
      {
        list: [
          'The client must not have engaged in link exchanges with link farms or spamming techniques that could harm rankings.',
          'While we strive to deliver positive outcomes, we cannot guarantee specific search engine results, traffic increases, sales, or ranking positions.',
          'All fees are payable in advance and are non-refundable unless otherwise agreed in writing or required by law.',
          'If the client modifies optimized pages created by Go Digital Step or fails to implement recommended changes, any guarantees or service commitments tied to those recommendations may be voided.',
          'Go Digital Step follows ethical SEO practices and may void guarantees if the client engages in hidden links, link farms, page redirects, cloaking, automated submission software, or similar undesirable actions.',
        ],
      },
    ],
  },
  {
    id: 'client-information',
    title: 'Use of Client Information',
    body: [
      'Go Digital Step may use client websites, designs, layouts, and collateral in marketing initiatives, including portfolio examples, case studies, and other promotional materials, unless the client specifically requests otherwise in writing.',
    ],
  },
  {
    id: 'client-access',
    title: 'Assignment of Specific Rights',
    body: [
      'To receive professional SEO and digital marketing services, the client agrees to provide the access and permissions reasonably necessary for Go Digital Step to perform the work, including:',
      {
        list: [
          'FTP, CMS, hosting, analytics, or other back-end administrative access needed for on-page optimization.',
          'Permission to communicate with third parties such as web designers, developers, or vendors when necessary.',
          'Access to existing website traffic statistics and performance data.',
          'Authorization to use client logos, trademarks, website images, and content for SEO and campaign purposes.',
          'Additional text, creative, or business information when required for content generation, optimization, or campaign execution.',
        ],
      },
    ],
  },
  {
    id: 'confidentiality',
    title: 'Non-Disclosure and Confidentiality',
    body: [
      'The client agrees to maintain strict confidentiality of non-public information belonging to Go Digital Step. The client may not use or disclose such information without written consent.',
      {
        list: [
          'The client agrees not to publicly attack or criticize Go Digital Step, its employees, associates, or partners in a way intended to damage reputation rather than resolve a service concern.',
          'The client agrees not to seek or present SEO advice in a manner that misrepresents Go Digital Step or harms its reputation.',
          'In the event of a breach of this agreement, the client may be responsible for reasonable damages resulting from the breach.',
        ],
      },
    ],
  },
  {
    id: 'termination',
    title: 'Termination or Cancellation',
    body: [
      'A contract terminates when the specified obligations are fulfilled or if either party breaches the contract. A contract may also be terminated by mutual agreement or if performance becomes impossible due to circumstances outside the parties’ reasonable control.',
      'Contracts may be canceled with 30 days written notice after the initial 30 days, unless a separate agreement states otherwise.',
    ],
  },
  {
    id: 'copyright',
    title: 'Copyright Protection',
    body: [
      'Go Digital Step retains copyright protection for materials, information, and publications posted on its website.',
      {
        list: [
          'Clients may download, copy, and distribute website information for non-commercial internal use while retaining copyright notices and without modification.',
          'Except for fair use under copyright law, clients may not use, copy, or distribute website information without prior written consent.',
        ],
      },
    ],
  },
  {
    id: 'subscriptions',
    title: 'Subscription and Billing',
    body: [
      'All subscriptions are recurring and automatically renew at the end of each paid subscription period unless canceled under the applicable cancellation policy.',
      {
        list: [
          'It is the client’s responsibility to understand Go Digital Step features, limitations, and process requirements before selecting a plan.',
          'No refunds will be issued for lack of understanding, compatibility issues, partial months of service, canceled subscription plans, or upgrade/downgrade changes unless required by law or agreed in writing.',
          'Subscription fees are charged at regular intervals until the client cancels the subscription.',
        ],
      },
      'Subscriptions can be canceled by emailing at least three days before the next billing date. A confirmation receipt will be sent upon cancellation.',
    ],
  },
  {
    id: 'refunds',
    title: 'Refund Policy',
    body: [
      'All subscriptions are billed in advance every month and are non-refundable. No refunds will be issued for canceled subscription plans, partial months of service, or upgrade/downgrade refunds unless required by applicable law or stated in a separate written agreement.',
    ],
  },
  {
    id: 'acknowledgement',
    title: 'Acknowledgement',
    body: [
      'By using our services, you acknowledge that you have read, understood, and agree to these Terms and Conditions.',
      'For questions about these terms, please contact:',
      { contact: { email: 'info@godigitalstep.com', location: 'India' } },
    ],
  },
];

function renderBody(body) {
  return body.map((block, i) => {
    if (typeof block === 'string') {
      return <p key={i} className="text-slate-600 dark:text-slate-400 leading-relaxed mb-3">{block}</p>;
    }
    if (block.list) {
      return (
        <ul key={i} className="space-y-1.5 mb-3 ml-1">
          {block.list.map((item, j) => (
            <li key={j} className="flex items-start gap-2 text-slate-600 dark:text-slate-400">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-violet-400 shrink-0" />
              <span className="leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
      );
    }
    if (block.bullets) {
      return (
        <ul key={i} className="space-y-2 mb-3">
          {block.bullets.map(([label, desc], j) => (
            <li key={j} className="flex items-start gap-2 text-slate-600 dark:text-slate-400">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-violet-400 shrink-0" />
              <span className="leading-relaxed"><strong className="text-slate-800 dark:text-slate-200">{label}:</strong> {desc}</span>
            </li>
          ))}
        </ul>
      );
    }
    if (block.contact) {
      return (
        <div key={i} className="mt-2 p-4 rounded-xl bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800 flex items-start gap-3">
          <Mail className="w-5 h-5 text-violet-500 mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-slate-800 dark:text-slate-200">GDS – Go Digital Step</p>
            <a href={`mailto:${block.contact.email}`} className="text-violet-600 hover:underline">{block.contact.email}</a>
            <p className="text-slate-500 dark:text-slate-400">{block.contact.location}</p>
          </div>
        </div>
      );
    }
    return null;
  });
}

export default function TermsOfServicePage() {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) { sectionRefs.current[id] = el; observer.observe(el); }
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    setActiveId(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 gds-glass border-b border-slate-200/50 dark:border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-xl">GDS</span>
          </Link>
          <div className="flex items-center gap-4">
            <Button variant="ghost" asChild><Link to="/login">Sign In</Link></Button>
            <Button className="gds-btn-primary" asChild><Link to="/register">Get Started</Link></Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="pt-16 bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700">
        <div className="max-w-7xl mx-auto px-6 py-16 flex items-center gap-6">
          <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
            <FileText className="w-7 h-7 text-white" />
          </div>
          <div>
            <p className="text-violet-200 text-sm font-medium mb-1">Legal</p>
            <h1 className="font-heading font-bold text-4xl text-white">Terms and Conditions</h1>
            <p className="text-violet-200 text-sm mt-1">Last updated: {LAST_UPDATED}</p>
          </div>
        </div>
      </div>

      {/* Intro banner */}
      <div className="bg-violet-50 dark:bg-violet-950/30 border-b border-violet-100 dark:border-violet-900">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed max-w-3xl">
            Please read these Terms and Conditions carefully. They govern your use of Go Digital Step services,
            including SEO, Google Maps Marketing, content creation, website development, and related digital
            marketing work.
          </p>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex gap-12">

          {/* Sticky sidebar TOC */}
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-24">
              <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-4">
                On This Page
              </p>
              <nav className="space-y-1">
                {SECTIONS.map(({ id, title }, idx) => (
                  <button
                    key={id}
                    onClick={() => scrollTo(id)}
                    className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
                      activeId === id
                        ? 'bg-violet-50 dark:bg-violet-950/50 text-violet-600 dark:text-violet-400 font-medium'
                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                    }`}
                  >
                    <span className={`w-5 h-5 rounded-md text-xs font-bold flex items-center justify-center shrink-0 ${
                      activeId === id ? 'bg-violet-600 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                    }`}>
                      {idx + 1}
                    </span>
                    <span className="truncate">{title}</span>
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main content */}
          <main className="flex-1 min-w-0 space-y-6">
            {SECTIONS.map(({ id, title, body }, idx) => (
              <section
                key={id}
                id={id}
                className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden scroll-mt-28"
              >
                {/* Section header */}
                <div className="flex items-center gap-4 px-6 py-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                  <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 text-white text-xs font-bold flex items-center justify-center shrink-0">
                    {idx + 1}
                  </span>
                  <h2 className="font-heading font-semibold text-slate-900 dark:text-white">{title}</h2>
                </div>
                {/* Section body */}
                <div className="px-6 py-5 text-sm">
                  {renderBody(body)}
                </div>
              </section>
            ))}
          </main>
        </div>
      </div>

      <footer className="py-8 px-6 border-t border-slate-200 dark:border-slate-800 text-center text-sm text-slate-600 dark:text-slate-400">
        <p>© 2026 GDS - Go Digital Step. All rights reserved. &nbsp;·&nbsp;
          <Link to="/privacy-policy" className="hover:text-indigo-600">Privacy Policy</Link>
          &nbsp;·&nbsp;
          <Link to="/terms-and-conditions" className="hover:text-indigo-600">Terms and Conditions</Link>
        </p>
      </footer>
    </div>
  );
}
